import { useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import "dayjs/locale/id";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getAuthUser } from "../../services/auth";
import "./Lead.css";
dayjs.locale("id");

export default function LeadTable({
  leads,
  onEdit,
  onDelete,
  tableLoading,
  page,
  perPageSize,
  search,
}) {
  const columnHelper = createColumnHelper();
  const auth = getAuthUser();

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text, keyword) => {
    if (!keyword) return text;

    // Ambil hanya kata alphanumeric, abaikan simbol asing
    const keywords = Array.from(
      new Set(
        keyword
          .trim()
          .split(/\s+/)
          .filter((w) => /^[\w\d]+$/.test(w))
      )
    )
      .filter(Boolean)
      .map(escapeRegex);

    if (keywords.length === 0) return text;

    const regex = new RegExp(`(${keywords.join("|")})`, "gi");

    const parts = String(text).split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="highlight-lead-table">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const columns = [
    columnHelper.display({
      id: "no",
      header: "No",
      size: 50,
      cell: ({ row, table }) => {
        const pageIndex = table.options.meta?.page || 1;
        const perPageSize = table.options.meta?.perPageSize || 10;
        const no = row.index + 1 + (pageIndex - 1) * perPageSize;
        return <div className="text-center">{no}</div>;
      },
    }),
    columnHelper.accessor((row) => row.name, {
      header: "Nama",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),

    columnHelper.accessor("phone_number", {
      header: "No Telp",
      size: 200,
      cell: ({ getValue }) => {
        const encodedPhone = getValue();

        if (!encodedPhone || encodedPhone.trim() === "") {
          return (
            <div className="d-flex justify-content-center align-items-center text-muted">
              -
            </div>
          );
        }

        const handleClick = (e) => {
          e.preventDefault();

          let decodedPhone = "";
          try {
            decodedPhone = atob(encodedPhone);
          } catch (err) {
            console.error("Gagal decode:", err);
            alert("Nomor WA tidak valid");
            return;
          }

          const formattedPhone = decodedPhone.replace(/\D/g, "");
          const waLink = `https://wa.me/${
            formattedPhone.startsWith("0")
              ? "62" + formattedPhone.slice(1)
              : formattedPhone
          }`;

          window.open(waLink, "_blank", "noopener,noreferrer");
        };

        return (
          <button
            onClick={handleClick}
            className="btn btn-link text-success d-flex align-items-center justify-content-center gap-1 text-decoration-none"
          >
            <i className="fa-brands fa-whatsapp fs-3"></i>
            <span>Whatsapp</span>
          </button>
        );
      },
    }),

    columnHelper.accessor("klpd", {
      header: "KLPD",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.position?.initials, {
      header: "Jabatan",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.institution_or_business, {
      header: "Instansi/Satker",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.location, {
      header: "Lokasi",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {highlightText(value, search)}
          </div>
        );
      },
    }),

    auth?.role == "admin" &&
      columnHelper.display({
        id: "actions",
        header: "Aksi",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="btn-action-lead d-flex gap-1 justify-content-center flex-md-row flex-column">
              <button
                className="btn btn-sm btn-warning"
                onClick={() => onEdit(item)}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => onDelete(item.id)}
              >
                Hapus
              </button>
            </div>
          );
        },
        meta: {
          isSticky: true,
          className: "size-column-lead",
        },
      }),
  ].filter(Boolean);

  const table = useReactTable({
    data: leads,
    columns,
    meta: {
      page,
      perPageSize,
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(leads.length / 10),
  });

  return (
    <>
      {tableLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div style={{ maxHeight: "543px", overflowY: "auto" }}>
          <table className="table table-bordered table-striped">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className || ""}
                      style={{
                        width: header.getSize(),
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        background: "white",
                        zIndex: 2,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxHeight: "60px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          position: cell.column.columnDef.meta?.isSticky
                            ? "sticky"
                            : undefined,
                          right: cell.column.columnDef.meta?.isSticky
                            ? 0
                            : undefined,
                          background: "white",
                          zIndex: 1,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-3">
                    Tidak ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
